import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X as CloseIcon, Pencil } from 'lucide-react';

import { Avatar } from './Avatar';
import { Btn } from './Btn';
import { useAuth } from '../../context/AuthContext';
import { useGame } from '../../context/GameContext';
import {
  keepTable,
  listMyTables,
  findExactRoster,
  resolveGameId,
  TablesError,
  type Table,
} from '../../services/tablesService';

/**
 * "KEEP THIS TABLE?"
 *
 * The single most important component in the feature, and the reason it either
 * lives or dies: **a table crystallises OUT of a game. It is never a setup
 * chore.** Nobody wants to create a group before they are allowed to play. So
 * there is no "new table" button anywhere in this app — there is only this
 * card, and it appears on the results screen of a night that already happened.
 *
 * Which means the copy here is doing real work. On tap, the server walks
 * backwards through `gameRecords` and files every past night this same roster
 * already played, so the table is born with its album full:
 *
 *      "The Saeeds — 4 nights together already."
 *
 * The app noticed the family before the family told it anything. That is
 * RECOGNITION, not admin, and every word below is chosen to land it that way.
 *
 * House rules, enforced here:
 *   · Quiet. It sits under the share sheet, below everything else on the
 *     results screen. It is not a modal, it never blocks Play again, and it
 *     never animates in front of anything.
 *   · Dismissible, and the dismissal STICKS — ✕ suppresses this exact roster
 *     until they have played together twice more (§2.1 of the design).
 *   · It only ever offers. It never nags.
 *
 * ── ONE HONEST LIMITATION ──────────────────────────────────────────────────
 * "≥2 signed-in humans" cannot be settled on the client: the results payload
 * carries names and avatars, not account status, and there is no endpoint that
 * would tell us (the backend is done and correct — this is not a gap in it).
 * So the gate here is the one the client CAN see — ≥2 non-bot players — and the
 * server is the authority. If the roster turns out to be one account and three
 * guests, `POST /api/tables` returns `needs-two-signed-in-humans` and the card
 * says so plainly instead of failing silently. Optimistic offer, honest refusal.
 */

interface KeepTablePlayer {
  playerId: string;
  playerName: string;
  playerAvatar: string;
}

export interface KeepTableCardProps {
  /** The game record id. Null until the server has written the record — the
   *  card simply doesn't render until it exists, because `keepTable` needs it. */
  gameId: string | null;
  players: KeepTablePlayer[];
  /** Solo games are never tables. */
  isSolo?: boolean;
}

const DISMISS_KEY = 'wr.keepTable.dismissed';
/** ✕ means "not these people, not yet" — we ask again after two more nights. */
const NIGHTS_BEFORE_ASKING_AGAIN = 2;

const firstName = (n: string) => String(n || 'Player').trim().split(/\s+/)[0];

/** A stable id for a roster, order-independent. */
const rosterKeyOf = (uids: string[]) => [...uids].sort().join('|');

function readDismissals(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function writeDismissals(map: Record<string, number>) {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(map));
  } catch {
    /* private mode — the offer simply reappears. Harmless. */
  }
}

type Mode = 'checking' | 'offer' | 'naming' | 'creating' | 'kept' | 'refused' | 'hidden';

export function KeepTableCard({ gameId, players, isSolo }: KeepTableCardProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { state } = useGame();

  const [mode, setMode] = useState<Mode>('checking');
  /** The record id we will actually keep. See `resolveGameId` — `gameId` (from
   *  `shareReady`) is null in practice, so the room code is the real source. */
  const [recordId, setRecordId] = useState<string | null>(gameId);
  const [name, setName] = useState('');
  const [kept, setKept] = useState<{ table: Table; gamesFiled: number } | null>(null);
  const [existing, setExisting] = useState<Table | null>(null);
  const [refusal, setRefusal] = useState<string>('');

  /** Bots are not people, and they are structurally excluded from tables. */
  const humans = useMemo(
    () => players.filter(p => p.playerId && !String(p.playerId).startsWith('bot_')),
    [players],
  );
  const roster = useMemo(() => humans.map(p => p.playerId), [humans]);
  const rosterKey = useMemo(() => rosterKeyOf(roster), [roster]);

  const me = currentUser?.uid;
  const isGuest = !currentUser || (currentUser as any).isAnonymous;

  /** The name the family would have picked. 2 → "You & Mom". 3+ → "Arslan's table". */
  const defaultName = useMemo(() => {
    const others = humans.filter(p => p.playerId !== me);
    if (humans.length === 2 && others[0]) return `You & ${firstName(others[0].playerName)}`;
    const mine = humans.find(p => p.playerId === me);
    return `${firstName(mine?.playerName || 'My')}'s table`;
  }, [humans, me]);

  const roomCode = state.roomCode || '';

  useEffect(() => {
    let cancelled = false;

    // The gates the client can actually see. A guest has no account to seat, a
    // solo game is not a night, and two people is the floor for a table.
    // (Note we gate on the ROOM, not on `gameId` — the id is resolved below.)
    if (isSolo || isGuest || (!gameId && !roomCode) || humans.length < 2) {
      setMode('hidden');
      return;
    }

    // "Not these people, not yet." Honour it, and count down to asking again.
    const dismissals = readDismissals();
    const seen = dismissals[rosterKey];
    if (typeof seen === 'number') {
      if (seen + 1 < NIGHTS_BEFORE_ASKING_AGAIN) {
        writeDismissals({ ...dismissals, [rosterKey]: seen + 1 });
        setMode('hidden');
        return;
      }
      // Two more nights have passed. Ask again, once.
      const next = { ...dismissals };
      delete next[rosterKey];
      writeDismissals(next);
    }

    (async () => {
      try {
        // Already kept? Then this night files itself and there is nothing to ask.
        const mine = await listMyTables();
        if (cancelled) return;
        const already = findExactRoster(mine, rosterKey.split('|').filter(Boolean));
        if (already) {
          setExisting(already);
          setMode('hidden');
          return;
        }

        // The record is written as the game ends; resolve its id before offering,
        // so that tapping Keep can never fail for want of one.
        const id = gameId || (await resolveGameId(roomCode));
        if (cancelled) return;
        if (!id) {
          setMode('hidden');
          return;
        }

        setRecordId(id);
        setName(defaultName);
        setMode('offer');
      } catch {
        // No API, no offer. Never an error state on the winner's screen.
        if (!cancelled) setMode('hidden');
      }
    })();

    return () => { cancelled = true; };
    // PRIMITIVES ONLY. `roster` is an array, and an array in a dependency list is
    // an identity check: if the `players` prop is rebuilt on any render — which
    // it is, it comes straight off a socket payload — the effect refires, calls
    // setState, renders, refires… "Maximum update depth exceeded", on the winner's
    // screen. `rosterKey` is the same information as a stable string, and the
    // roster is recovered from it inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, roomCode, rosterKey, isGuest, isSolo, defaultName]);

  const dismiss = () => {
    const dismissals = readDismissals();
    writeDismissals({ ...dismissals, [rosterKey]: 0 });
    setMode('hidden');
  };

  const create = async () => {
    if (!recordId) return;
    setMode('creating');
    try {
      const res = await keepTable(recordId, { name: name.trim() || defaultName });
      setKept({ table: res.table, gamesFiled: res.backfill?.gamesFiled ?? 0 });
      setMode('kept');
    } catch (e) {
      const err = e as TablesError;

      // They already kept it (maybe on another device, a second ago). Not an
      // error the user caused — hand them the table they already have.
      if (err.code === 'table-exists' && err.body?.table) {
        setExisting(err.body.table as Table);
        setMode('hidden');
        return;
      }

      setRefusal(
        err.code === 'needs-two-signed-in-humans'
          ? 'A table needs two people with accounts. Your guests can sign in and keep their seat.'
          : err.code === 'name-rejected'
            ? 'Try another name for the table.'
            : 'Could not keep the table just now.',
      );
      setMode('refused');
    }
  };

  if (mode === 'hidden' || mode === 'checking') {
    // Silently filed under a table they already have. One line, no decision.
    if (existing) {
      return (
        <Shell>
          <span style={{ fontSize: 13, color: 'var(--ink-3)', flex: 1, minWidth: 0 }}>
            Filed to <strong style={{ color: 'var(--ink)' }}>{existing.emoji} {existing.name}</strong>
          </span>
          <button type="button" onClick={() => navigate(`/tables/${existing.id}`)} style={linkBtn}>
            Open →
          </button>
        </Shell>
      );
    }
    return null;
  }

  if (mode === 'refused') {
    return (
      <Shell>
        <span style={{ fontSize: 13, color: 'var(--ink-3)', flex: 1, lineHeight: 1.4 }}>{refusal}</span>
        <button type="button" onClick={() => setMode('hidden')} aria-label="Dismiss" style={iconBtn}>
          <CloseIcon size={14} />
        </button>
      </Shell>
    );
  }

  /* THE PAYOFF. The table arrives with its album already full. */
  if (mode === 'kept' && kept) {
    const nights = kept.gamesFiled;
    return (
      <Shell accent>
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 15 }}>
            {kept.table.emoji} {kept.table.name}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.4 }}>
            {nights > 0
              ? `${nights} night${nights === 1 ? '' : 's'} together already — they're all in the album.`
              : 'Tonight is in the album. Same people, one tap next time.'}
          </div>
        </motion.div>
        <Btn kind="primary" icon="ArrowRight" onClick={() => navigate(`/tables/${kept.table.id}`)}>
          Open
        </Btn>
      </Shell>
    );
  }

  const avatars = humans.slice(0, 5);

  return (
    <Shell>
      <div className="wr-col gap-3" style={{ flex: 1, minWidth: 0 }}>
        <div className="wr-row gap-3" style={{ alignItems: 'center' }}>
          {/* The party photo. These are the people who were here. */}
          <span className="wr-row" style={{ flexShrink: 0 }}>
            {avatars.map((p, i) => (
              <span key={p.playerId} style={{ marginLeft: i === 0 ? 0 : -10, display: 'inline-flex' }}>
                <Avatar id={p.playerAvatar} size={30} />
              </span>
            ))}
          </span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 15 }}>
              Keep this table?
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.4 }}>
              Same people, one tap next time — and tonight goes in the album.
            </div>
          </div>

          {mode === 'offer' && (
            <button type="button" onClick={dismiss} aria-label="Not now" style={iconBtn}>
              <CloseIcon size={14} />
            </button>
          )}
        </div>

        {mode === 'naming' ? (
          <div className="wr-row gap-2">
            <input
              className="field"
              value={name}
              autoFocus
              maxLength={40}
              aria-label="Table name"
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void create(); }}
              style={{ flex: 1, minWidth: 0, fontFamily: 'var(--display)', fontWeight: 600 }}
            />
            <Btn kind="primary" onClick={create}>Keep</Btn>
          </div>
        ) : (
          <div className="wr-row gap-2" style={{ alignItems: 'center' }}>
            <Btn
              kind="primary"
              onClick={create}
              disabled={mode === 'creating'}
              icon={mode === 'creating' ? undefined : 'ArrowRight'}
            >
              {mode === 'creating' ? 'Keeping…' : `Keep as "${name}"`}
            </Btn>
            <button
              type="button"
              onClick={() => setMode('naming')}
              aria-label="Rename this table"
              disabled={mode === 'creating'}
              style={{ ...iconBtn, width: 40, height: 40 }}
            >
              <Pencil size={14} />
            </button>
          </div>
        )}
      </div>
    </Shell>
  );
}

/* The card never shouts. No shadow, no gradient, no confetti of its own — the
   winner already owns this screen and the offer is a guest on it. */
function Shell({ children, accent }: { children: React.ReactNode; accent?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2 }}
      className="card"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 14,
        marginTop: 16,
        maxWidth: 460,
        marginLeft: 'auto',
        marginRight: 'auto',
        boxShadow: 'none',
        background: accent ? 'var(--accent-soft)' : 'var(--surface)',
        border: accent ? '1.5px solid var(--accent)' : '1px solid var(--line)',
      }}
    >
      {children}
    </motion.div>
  );
}

const iconBtn: React.CSSProperties = {
  width: 44,
  height: 44,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  color: 'var(--ink-3)',
  cursor: 'pointer',
  flexShrink: 0,
};

const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--accent)',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'var(--ui)',
  minHeight: 44,
  flexShrink: 0,
};

export default KeepTableCard;
